import { Location } from '@angular/common';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { LangCode } from 'src/app/models/localization.model';

// values should be all lower case for consistency
export enum ActionsQueryParamKeys {
  AUTO_START_CALL = 'startcall',
  DEFAULT_GALLERY_VIEW = 'gv',
  SKIP_DEVICE_SETUP = 'skipdevicesetup',
  /** Embed shell (?standalone=true): hides leave-space (shows contact-support instead), invite, and copy-link in top controls; also hides attendance "Create Event". Can also be enabled via institution hideSpaceBackLinks unless ?standalone=false. */
  STAND_ALONE_MODE = 'standalone',
  OBSERVER_MODE = 'observer',
  LANGUAGE = 'language',
  BROADCAST_EVENTS = 'broadcastevents',
  POST_INSTITUTION_UPGRADE = 'institutionupgraded',
  AUTO_LOCAL_RECORDING = 'startlocalrecording',
  SKIP_CALL_HOST_CHECK = 'skipcallhostcheck',
  /** Space layout/behavior via ?mode= (see SpaceViewMode). Independent of standalone embed chrome. */
  MODE = 'mode',
}

/** Space view modes passed via ?mode= (values are lower case). */
export enum SpaceViewMode {
  /** Hide Pencil call UI and call prompts; users rely on the host meeting for audio/video. */
  WHITEBOARD_ONLY = 'whiteboardonly',
}

export enum ActionsQueryParamValues {
  TRUE = 'true',
  FALSE = 'false',
}

/**
 * Query params for third-party meeting embed entry (Teams, Zoom).
 * Both keys are set together for meeting tabs; they overlap in intent (stay in the host meeting)
 * but control different layers:
 * - standalone: embed shell — no leave/invite/copy-link chrome
 * - mode=whiteboardonly: hide Pencil call stack — A/V stays in Teams/Zoom
 */
export const MEETING_EMBED_SPACE_QUERY_PARAMS: { [K in ActionsQueryParamKeys]?: string } = {
  [ActionsQueryParamKeys.STAND_ALONE_MODE]: ActionsQueryParamValues.TRUE,
  [ActionsQueryParamKeys.MODE]: SpaceViewMode.WHITEBOARD_ONLY,
};

export function extractActionsQueryParams(): Record<string, string> {
  const params: Record<string, string> = {};
  new URLSearchParams(location.search).forEach((value, key) => {
    const sanitizedKey = sanitizeActionsQueryParam(key);
    if (isActionsQueryParamKey(sanitizedKey)) {
      params[sanitizedKey] = sanitizeActionsQueryParam(value);
    }
  });
  return params;
}

export function sanitizeActionsQueryParam(param: string): string {
  return param.toLowerCase().trim();
}

export function isActionsQueryParamKey(sanitizedParam: string): boolean {
  return (Object.values(ActionsQueryParamKeys) as string[]).includes(sanitizedParam);
}

// doesn't include the question mark.
export function getActionsSearchQuery(): string {
  return new URLSearchParams(extractActionsQueryParams()).toString();
}

export function removeQueryParamsFromUrl(
  locationService: Location,
  telemetryService: TelemetryService,
  queryParamsToBeDeleted: ActionsQueryParamKeys[],
): void {
  const currentQueryParams = extractActionsQueryParams();

  const hasParamsToDelete: boolean = !!queryParamsToBeDeleted.find(
    (param) => param in currentQueryParams,
  );
  if (!hasParamsToDelete) {
    return;
  }

  const newQueryParams = new URLSearchParams(currentQueryParams);
  queryParamsToBeDeleted.forEach((param) => {
    newQueryParams.delete(param);
  });

  const currentState = locationService.getState();
  try {
    locationService.replaceState(location.pathname, newQueryParams.toString(), currentState);
  } catch (error) {
    telemetryService.errorEvent('Failed_to_replace_state_after_changing_language', error);
  }
}

export function checkIfQueryParamSetToTrue(queryParam: ActionsQueryParamKeys): boolean {
  const queryParams = extractActionsQueryParams();

  return queryParams[queryParam] === ActionsQueryParamValues.TRUE;
}

export function checkIfQueryParamSetToFalse(queryParam: ActionsQueryParamKeys): boolean {
  const queryParams = extractActionsQueryParams();

  return queryParams[queryParam] === ActionsQueryParamValues.FALSE;
}

/**
 * The query param overrides the institution setting: either the param is set to true, or the
 * institution's hideSpaceBackLinks is on and the param is not set to false.
 * Lives here rather than only in ActionsQueryParamsService so that service's own dependencies can
 * resolve standalone mode without a circular dependency.
 */
export function checkIfStandAloneMode(hideSpaceBackLinks: boolean | undefined): boolean {
  return (
    checkIfQueryParamSetToTrue(ActionsQueryParamKeys.STAND_ALONE_MODE) ||
    (Boolean(hideSpaceBackLinks) &&
      !checkIfQueryParamSetToFalse(ActionsQueryParamKeys.STAND_ALONE_MODE))
  );
}

export function checkIfSpaceViewMode(mode: SpaceViewMode): boolean {
  const queryParams = extractActionsQueryParams();

  return queryParams[ActionsQueryParamKeys.MODE] === mode;
}

// this was added here to break a circular dependency if the user service relied on the actions query params service
// if the lang query param is not set or not valid it will return an empty string
// otherwise it will return the selected -original not sanitized- language code
export function getLanguageQueryParam(enabledLangs: LangCode[]): string {
  const queryParams = extractActionsQueryParams();
  const sanitizedLanguageCode = queryParams[ActionsQueryParamKeys.LANGUAGE];

  if (!sanitizedLanguageCode) {
    return '';
  }

  const selectedLang = enabledLangs.find(
    (lang) => sanitizeActionsQueryParam(lang.code) === sanitizedLanguageCode,
  );
  return selectedLang?.code ?? '';
}
